/* eslint-disable max-len */
import type { FC } from "react";

import { NoData } from "pro-features/components/NoData/src/NoData";
import {
    BacklinksOverviewBodyContainer,
    BacklinksOverviewBottomWidgetContainer,
    WidgetsRow,
} from "app/pages/digital-marketing/backlinks/reports/overview/BacklinksOverviewStyledComponents";
import { BacklinksReferringOvertimeChart } from "app/pages/digital-marketing/backlinks/reports/overview/components/BacklinksOvertimeChart/BacklinksReferringOvertimeChart";
import { BacklinksCountriesTableWidget } from "app/pages/digital-marketing/backlinks/reports/overview/components/CountriesWidget/BacklinksCountriesTableWidget";
import { BacklinksNewLostCharts } from "app/pages/digital-marketing/backlinks/reports/overview/components/NewLostChart/BacklinksNewLostCharts";
import { BacklinksTLDsWidget } from "app/pages/digital-marketing/backlinks/reports/overview/components/TLDsWidget/BacklinksTLDsWidget";

import type { BacklinksTimeseriesListParams } from "@similarweb/pro-api-client";

import {
    useBacklinksSummaryApi,
    useBacklinksTimeseriesApi,
    useBacklinksTimeseriesNewLostApi,
} from "../../shared/backlinks-api-service";
import { API_DATE_REQUEST_FORMAT, START_DATE_13_M, TODAY } from "../../shared/BacklinksConsts";
import { useExpectedBacklinksParams } from "../../shared/BacklinksUtils";

import { BacklinksOverviewSummaryMetrics } from "./components/backlinks-overview-report-sections/backlinks-overview-summary-metrics";

export const BacklinksOverviewReport: FC = () => {
    const params = useExpectedBacklinksParams();
    const from = START_DATE_13_M.format(API_DATE_REQUEST_FORMAT);
    const to = TODAY.format(API_DATE_REQUEST_FORMAT);
    const overviewTimeseriesParams = {
        from,
        to,
        isDaily: true,
    } satisfies Pick<BacklinksTimeseriesListParams, "from" | "to" | "isDaily">;

    const { data: summaryData, isLoading: isSummaryDataLoading } = useBacklinksSummaryApi({
        key: params.key ?? params.pageFilterParsed?.url,
        status: params.status ?? "All",
    });
    const { data: seriesData = [], isLoading: isSeriesDataLoading } = useBacklinksTimeseriesApi({
        ...overviewTimeseriesParams,
        key: params.key,
    });
    const { data: seriesNewLostData = [], isLoading: isSeriesNewLostDataLoading } = useBacklinksTimeseriesNewLostApi({
        ...overviewTimeseriesParams,
        key: params.key,
    });

    if (
        !summaryData &&
        seriesData.length === 0 &&
        seriesNewLostData.length === 0 &&
        !isSummaryDataLoading &&
        !isSeriesDataLoading &&
        !isSeriesNewLostDataLoading
    ) {
        return (
            <NoData
                title="digitalsuite.backlinks.common.no_data.title"
                subtitle="digitalsuite.backlinks.common.no_data.subtitle"
            />
        );
    }

    return (
        <BacklinksOverviewBodyContainer>
            <BacklinksOverviewSummaryMetrics />
            <BacklinksReferringOvertimeChart />
            <BacklinksNewLostCharts />
            <WidgetsRow>
                <BacklinksOverviewBottomWidgetContainer>
                    <BacklinksCountriesTableWidget />
                </BacklinksOverviewBottomWidgetContainer>
                <BacklinksOverviewBottomWidgetContainer>
                    <BacklinksTLDsWidget />
                </BacklinksOverviewBottomWidgetContainer>
            </WidgetsRow>
        </BacklinksOverviewBodyContainer>
    );
};
